import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { UiCatalogueModel } from '@nightshift/ui-types';

import { Empty, Kicker, Spinner } from '../components/primitives.tsx';
import { RouteListEditor, type RouteMachine, type RouteRow } from '../components/route-list-editor.tsx';
import { api, type SessionUser } from '../live/api.ts';
import { useLive, useStore } from '../live/context.tsx';
import { canWriteSettings } from '../lib/privilege.ts';
import { routePhrase } from '../lib/route-list.ts';
import type { SidecarRoutePayload, SidecarSettingsPayload } from '../lib/sidecar-settings.ts';
import { registerUnsavedEditor } from '../lib/unsaved.ts';
import { announceSettingsSaved, useSettingsAutosave } from '../lib/settings-autosave.tsx';
import './sidecars-settings.css';

type ListKey = 'footprintRoutes' | 'scanRoutes' | 'textRoutes';

const LISTS: ReadonlyArray<{ key: ListKey; label: string; help: string }> = [
  {
    key: 'footprintRoutes',
    label: 'Footprint',
    help: 'Estimates which files a ticket will touch. Runs once per brief revision.',
  },
  {
    key: 'scanRoutes',
    label: 'Comparison',
    help: 'Compares a ticket against the others to propose dependencies and overlaps.',
  },
  {
    key: 'textRoutes',
    label: 'Text tasks',
    help: 'Short card titles and summaries. At most two new requests per pass; never alters the brief.',
  },
];

function toRow(route: SidecarRoutePayload): RouteRow {
  return { machine: route.machine ?? null, harness: route.harness, model: route.model, thinking: route.effort };
}

function toPayload(row: RouteRow): SidecarRoutePayload {
  return { machine: row.machine, harness: row.harness, model: row.model, effort: row.thinking };
}

type Draft = Record<ListKey, RouteRow[]>;

function draftOf(settings: SidecarSettingsPayload['settings']): Draft {
  const own = settings.own;
  return {
    footprintRoutes: (own?.footprintRoutes ?? settings.footprintRoutes ?? []).map(toRow),
    scanRoutes: (own?.scanRoutes ?? settings.scanRoutes ?? []).map(toRow),
    textRoutes: (own?.textRoutes ?? settings.textRoutes ?? []).map(toRow),
  };
}

/**
 * Sidecars for one project: whether they run, on what, and how much they may spend.
 *
 * The switch is off by default and the panel says so plainly, because the honest default for
 * something that spends money on every ticket is off. The order of the controls follows the
 * order of the questions an operator actually asks: is this on, what does it run on, what is
 * it allowed to cost. The three route lists share one Save and one unsaved-edit guard.
 */
export function SidecarsSettingsPanel({ user }: { user: SessionUser | null }) {
  const store = useStore();
  const state = useLive();
  const canWrite = canWriteSettings(user);
  const project = state.projectSlug;

  const [payload, setPayload] = useState<SidecarSettingsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [machines, setMachines] = useState<RouteMachine[]>([]);
  const [models, setModels] = useState<UiCatalogueModel[]>([]);
  const savedDraft = useMemo(() => (payload ? JSON.stringify(draftOf(payload.settings)) : null), [payload]);
  const dirty = draft !== null && savedDraft !== null && JSON.stringify(draft) !== savedDraft;
  const dirtyRef = useRef(false);
  const routeSaveInFlight = useRef(false);
  dirtyRef.current = dirty;

  const load = useCallback(async () => {
    if (!project) {
      setPayload(null);
      setDraft(null);
      return;
    }
    try {
      const next = await api.sidecarSettings(project);
      setPayload(next);
      // Only replace a draft the operator has not touched.
      if (!dirtyRef.current) setDraft(draftOf(next.settings));
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, [project]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([api.machines().catch(() => null), api.modelsCatalogue(project).catch(() => null)]).then(
      ([hosts, catalogue]) => {
        if (cancelled) return;
        setMachines(
          (hosts?.items ?? []).map((item) => ({ name: item.name, installedHarnesses: item.installedHarnesses, live: item.live, paused: item.paused })),
        );
        setModels(catalogue?.models ?? []);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [project]);

  useEffect(
    () => registerUnsavedEditor({ isDirty: () => dirtyRef.current, what: 'the sidecar routes' }),
    [],
  );

  const routesIncomplete = draft !== null && LISTS.some(({ key }) => draft[key].some((row) => !row.harness || !row.model));
  useEffect(() => {
    if (!canWrite || !dirty || routesIncomplete) return;
    const timer = setTimeout(() => void saveRoutes().catch(() => undefined), 700);
    return () => clearTimeout(timer);
    // Draft changes restart the timer; a save response restarts it for edits made in flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canWrite, dirty, draft, routesIncomplete, savedDraft]);

  if (!project) {
    return (
      <Empty
        title="Select a project."
        body="Sidecars are configured per project: the routes, the cap and the switch all belong to one."
      />
    );
  }

  if (error && !payload) {
    return (
      <Empty
        title="Sidecar settings could not be read."
        body={error}
        action={
          <button type="button" className="btn" onClick={() => void load()}>
            Try again
          </button>
        }
      />
    );
  }

  if (!payload || !draft) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--s8)' }}>
        <Spinner size={20} />
      </div>
    );
  }

  const { settings, budget, defaults } = payload;
  const utility = (settings.utilityRoutes ?? []).map(toRow);

  async function save(patch: Record<string, unknown>, label: string, submittedRoutes?: Draft): Promise<void> {
    if (!project) return;
    setBusy(true);
    try {
      const next = { ...payload!, ...(await api.saveSidecarSettings(project, patch)) };
      setPayload(next);
      if (submittedRoutes) {
        setDraft((current) => {
          if (JSON.stringify(current) !== JSON.stringify(submittedRoutes)) return current;
          const saved = draftOf(next.settings);
          return JSON.stringify(current) === JSON.stringify(saved) ? current : saved;
        });
      }
      setError(null);
      announceSettingsSaved(label);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
      throw caught;
    } finally {
      setBusy(false);
    }
  }

  const saveRoutes = async (): Promise<void> => {
    if (routeSaveInFlight.current || !draft) return;
    routeSaveInFlight.current = true;
    const submitted = draft;
    try {
      await save(
        {
          footprintRoutes: submitted.footprintRoutes.map(toPayload),
          scanRoutes: submitted.scanRoutes.map(toPayload),
          textRoutes: submitted.textRoutes.map(toPayload),
        },
        'Sidecar routes saved',
        submitted,
      );
    } finally {
      routeSaveInFlight.current = false;
    }
  };

  const money = (value: number): string => `$${value.toFixed(2)}`;

  return (
    <section className="sheet-panel">
      <header className="panel-head">
        <Kicker>Sidecars</Kicker>
      </header>
      <div className="sheet-panel-body">
        <p className="cfg-help" style={{ marginTop: 0 }}>
          Cheap background jobs that estimate what each ticket will touch, compare tickets
          against each other and write card titles. They take no worker slot and hold up no
          ticket. Nothing runs until the switch is on.
        </p>

        <label className="cfg-field">
          <span>
            <span className="cfg-label">Run sidecars for this project</span>
            <p className="cfg-help">
              Off by default. While it is off nothing is queued and nothing is spent.
            </p>
          </span>
          <span className="cfg-control">
            <input
              type="checkbox"
              checked={settings.enabled}
              disabled={!canWrite || busy}
              onChange={(event) =>
                void save(
                  { enabled: event.target.checked },
                  event.target.checked ? 'Sidecars on for this project' : 'Sidecars off',
                ).catch(() => undefined)
              }
            />
          </span>
        </label>

        <p className="cfg-help">
          Routes are tried from top to bottom; the first available machine with capacity and the
          selected model runs the job. A list left empty inherits the utility routes under
          Nightshift → Models.
        </p>
        {LISTS.map(({ key, label, help }) => (
          <div className="route-block" key={key}>
            <div className="route-block-head">
              <Kicker>{label}</Kicker>
              <span className="cfg-help">{help}</span>
            </div>
            {/* Keep native selects enabled while an autosave is in flight so open menus stay open. */}
            <RouteListEditor
              rows={draft[key]}
              onChange={(rows) => setDraft((current) => current ? { ...current, [key]: rows } : current)}
              machines={machines}
              models={models}
              disabled={!canWrite}
              label={`${label} route`}
              anyMachineLabel="Any available machine"
              emptyText={
                <>
                  Inherits the Nightshift utility routes:{' '}
                  <strong>{utility.length > 0 ? utility.map(routePhrase).join(' → ') : 'none set'}</strong>.
                </>
              }
            />
          </div>
        ))}
        <div className="route-list-foot" style={{ marginTop: 'var(--s3)' }}>
          {dirty && !routesIncomplete ? <span className="cfg-help">Waiting to save route changes…</span> : null}
          {routesIncomplete ? <span className="cfg-error" role="alert">Unsaved: complete every route before it can be saved.</span> : null}
          {error && payload ? <span className="cfg-error" role="alert">Unsaved: {error}</span> : null}
        </div>

        <div className="cfg-grid">
        <NumberField
          label="Daily cap"
          help={`Soft: a job already admitted finishes. Spent ${money(budget.spentUsd)}, reserved ${money(
            budget.reservedUsd,
          )} today.`}
          value={settings.dailyCapUsd}
          step={0.5}
          placeholder={String(defaults.dailyCapUsd)}
          disabled={!canWrite || busy}
          onSave={(value) => save({ dailyCapUsd: value }, 'Daily cap saved')}
        />
        <NumberField
          label="Queue allowance"
          help="How many sidecar jobs this project may have waiting at once."
          value={settings.maxQueuedPerProject}
          step={1}
          placeholder={String(defaults.maxQueuedPerProject)}
          disabled={!canWrite || busy}
          onSave={(value) => save({ maxQueuedPerProject: value }, 'Queue allowance saved')}
        />
        <NumberField
          label="Job timeout (seconds)"
          help="Whole job, claim to result. Not per model invocation."
          value={Math.round(settings.jobTimeoutMs / 1000)}
          step={30}
          placeholder={String(Math.round(defaults.jobTimeoutMs / 1000))}
          disabled={!canWrite || busy}
          onSave={(value) => save({ jobTimeoutMs: value * 1000 }, 'Job timeout saved')}
        />
        <NumberField
          label="Scan quiet period (minutes)"
          help="A comparison waits until a ticket's text and footprints have not changed for this long. A ticket waiting to start Implementing is compared at once."
          value={Math.round((settings.scanQuietMs ?? defaults.scanQuietMs ?? 600_000) / 60_000)}
          step={1}
          placeholder={String(Math.round((defaults.scanQuietMs ?? 600_000) / 60_000))}
          disabled={!canWrite || busy}
          onSave={(value) => save({ scanQuietMs: Math.round(value * 60_000) }, 'Scan quiet period saved')}
        />
        </div>

        <div className="cfg-field">
          <span>
            <span className="cfg-label">Rescan all open tickets</span>
            <p className="cfg-help">
              Regenerates footprints, then compares dependencies. Resets failed retries. Budget and
              queue limits still apply, so requests may wait before jobs are admitted.
            </p>
          </span>
          <span className="cfg-control">
          <button
            type="button"
            className="btn"
            disabled={!canWrite || busy || !settings.enabled}
            onClick={() => {
              void (async () => {
                setBusy(true);
                try {
                  const result = await api.rescanProject(project);
                  store.toast('good', `Rescan requested for ${result.tickets} tickets: footprints, then dependencies. Failed retries reset; budget and queue limits still apply.`);
                } catch (caught) {
                  store.toast('bad', caught instanceof Error ? caught.message : String(caught));
                } finally {
                  setBusy(false);
                }
              })();
            }}
          >
            Scan project now
          </button>
          </span>
        </div>

        <div className="cfg-field sc-danger">
          <span>
            <span className="cfg-label">Clear results and start over</span>
            <p className="cfg-help">
              Deletes every footprint and every proposal for this project, including
              accepted and dismissed ones, and lets the same work be estimated again from
              scratch. What each job cost is kept, so today&rsquo;s cap still holds. Use it
              when you have changed something and want the next run measured on its own.
            </p>
          </span>
          <span className="cfg-control">
            <button
              type="button"
              className="btn btn-danger"
              disabled={!canWrite || busy}
              onClick={() => {
                if (
                  !window.confirm(
                    `Delete every footprint, proposal and decision for ${project}? ` +
                      'Detection starts again from nothing. This cannot be undone.',
                  )
                ) {
                  return;
                }
                void (async () => {
                  setBusy(true);
                  try {
                    const result = await api.resetSidecars(project);
                    store.toast(
                      'good',
                      `Cleared ${result.footprints} footprints and ${result.suggestions} proposals`,
                    );
                    await load();
                  } catch (caught) {
                    store.toast('bad', caught instanceof Error ? caught.message : String(caught));
                  } finally {
                    setBusy(false);
                  }
                })();
              }}
            >
              Clear results
            </button>
          </span>
        </div>
      </div>
    </section>
  );
}

function NumberField({
  label,
  help,
  value,
  step,
  placeholder,
  disabled,
  onSave,
}: {
  label: string;
  help: string;
  value: number;
  step: number;
  placeholder: string;
  disabled: boolean;
  onSave: (value: number) => Promise<void>;
}) {
  const autosave = useSettingsAutosave({
    value: String(value),
    save: async (text) => onSave(Number(text)),
    what: label,
    enabled: !disabled,
    delay: 500,
    validate: (text) => Number.isFinite(Number(text)) && Number(text) >= 0 ? null : 'Enter zero or a positive number.',
  });
  const { draft: text, setDraft: setText, status, error } = autosave;
  return (
    <div className="cfg-field">
      <div>
        <span className="cfg-label">{label}</span>
        <p className="cfg-help">{help}</p>
      </div>
      <div className="cfg-control">
        <input
          className="input num sc-num"
          type="number"
          step={step}
          min={0}
          value={text}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(event) => setText(event.target.value)}
        />
        {status === 'saving' ? <Spinner /> : null}
        {error ? <span className="cfg-error" role="alert">Unsaved: {error}</span> : null}
      </div>
    </div>
  );
}
